import React, { useEffect, useMemo, useState } from 'react';
import { AutoComplete, Form, Input, Select, Table, Tag } from 'tdesign-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import styled from 'styled-components';
import { trim } from 'lodash';
import { connect } from 'react-redux';
import { useMemoizedFn } from 'ahooks';
import { getAllTopics, getSubscriptionsPaginate } from '@/services/utilService';
import { renderByteRate, renderNum, renderNumRate, sleep, toTsOptions } from '@/utils/common';
import SvgIcon from '@/components/SvgIcon';
import useTable from '@/hooks/useTable';
import PopupLink from '@/components/Cluster/PopupLink';
import SystemSwitchAppend from '@/components/Cluster/SystemSwitchAppend';
import Detail from './Detail';
import Status from './Status';
import Dashboard from './Dashboard';
import Online from './Online';

const { FormItem } = Form;

function Subscription({ nsOptions }) {
  const [searchParams] = useSearchParams();
  const clusterName = searchParams.get('name');
  const [visible, setVisible] = useState(false);
  const [currentRow, setCurrentRow] = useState({});
  const [topicOptions, setTopicOptions] = useState([]);
  const [systemTopic, setSystemTopic] = useState(false);

  const navigate = useNavigate();

  // 查询初始化
  const initialData = useMemo(
    () => ({
      subscription: searchParams.get('subscription'),
      topic: searchParams.get('topic'),
      namespace: searchParams.get('namespace'),
    }),
    [searchParams],
  );

  const chartIconClick = (row) => {
    setVisible(true);
    setCurrentRow(row);
  };

  const columns = [
    {
      colKey: 'subscription',
      title: '订阅名',
      fixed: 'left',
      width: 260,
      cell: ({ row }) => <Detail resource={'pulsar:tool:message'} detail={row} />,
    },
    {
      colKey: 'topic',
      title: 'Topic',
      width: 400,
      cell: ({ row }) => (
        <PopupLink
          tip='跳转Topic'
          onClick={() => {
            navigate(`/cluster/topic/list?name=${clusterName}&topic=${row.topic}`);
          }}
        >
          {row.systemTopic && (
            <Tag style={{ marginRight: 6 }} theme='danger'>
              system
            </Tag>
          )}
          {row.topic}
        </PopupLink>
      ),
    },
    {
      colKey: 'msgBacklog',
      title: '积压条数',
      cell: renderNum,
      width: 120,
      sorter: true,
    },
    {
      colKey: 'rateOut',
      title: '消费速率',
      sortType: 'all',
      sorter: true,
      width: 120,
      cell: renderNumRate,
    },
    {
      colKey: 'bytesOut',
      title: '消费吞吐',
      sortType: 'all',
      sorter: true,
      cell: renderByteRate,
      width: 120,
    },
    {
      colKey: 'subscriptionState',
      title: '状态',
      cell: ({ row }) => (
        <Status active={row.subscriptionState}>{row.subscriptionState ? '在线' : '离线'}</Status>
      ),
      width: 100,
      sorter: true,
    },
    {
      colKey: 'subscriptionType',
      title: '订阅类型',
      sorter: true,
      width: 120,
    },
    {
      colKey: 'consumerOnlineCount',
      title: '在线个数',
      sorter: true,
      cell: ({ row }) => <Online clusterName={clusterName} detail={row} />,
      width: 120,
    },
    {
      colKey: 'app',
      title: '应用',
      width: 100,
      sorter: true,
    },
    {
      colKey: 'updateTime',
      title: '采集时间',
      cell: ({ row }) => dayjs(row.updateTime).format('YYYY-MM-DD HH:mm:ss'),
      width: 200,
      sorter: true,
    },
    {
      colKey: 'option',
      title: '操作',
      fixed: 'right',
      align: 'center',
      cell: ({ row }) => (
        <Icon type='presentation-chart-line' size={18} onClick={() => chartIconClick(row)} />
      ),
      width: 60,
    },
  ];

  const loadData = useMemoizedFn(async (params = {}) => {
    const { topic, ...others } = params;

    const { data } = await getSubscriptionsPaginate({
      ...others,
      topic: trim(topic),
      clusterName,
      containSystemTopic: systemTopic,
    });
    setData(data.items, data.count);
  });

  const namespaceChange = useMemoizedFn(async (value) => {
    form.setFieldsValue({ topic: undefined });
    setTopicOptions([]);
    if (value) {
      const resp = await getAllTopics({
        clusterName,
        namespace: value,
      });
      setTopicOptions(resp.data);
    }
  });

  const onSystemSwitch = async (isContainSystemTopic) => {
    setSystemTopic(isContainSystemTopic);
    await sleep(30);
    doSearch(null, true);
  };

  const { form, header, config, setData, doSearch } = useTable({
    columns,
    search: (
      <>
        <FormItem name='subscription'>
          <Input placeholder='请输入订阅名' clearable />
        </FormItem>
        <FormItem name='namespace'>
          <Select
            onChange={namespaceChange}
            options={toTsOptions(nsOptions)}
            placeholder='选择命名空间'
            clearable
          />
        </FormItem>
        <FormItem name='topic'>
          <AutoComplete
            style={{ width: '100%' }}
            popupProps={{ overlayInnerStyle: { width: 'auto' } }}
            options={topicOptions}
            placeholder='请输入Topic'
            clearable
          />
        </FormItem>
        <FormItem name='app'>
          <Input placeholder='请输入应用' clearable />
        </FormItem>
      </>
    ),
    onSearch: loadData,
    append: <SystemSwitchAppend value={systemTopic} onChange={onSystemSwitch} />,
    remote: true,
  });

  const doInit = useMemoizedFn(async () => {
    setSystemTopic(false);
    form.reset();
    await sleep(30);

    if (searchParams.size > 1) {
      // 有初始化条件
      doSearch(initialData);
      await namespaceChange(initialData.namespace);
      form.setFieldsValue(initialData);
    } else {
      doSearch();
    }
  });

  useEffect(() => {
    if (clusterName) {
      doInit();
    }
  }, [doInit, clusterName]);

  const handleClose = () => {
    setVisible(false);
  };

  return (
    <Root>
      {header}
      <Table {...config} />
      <Dashboard
        clusterName={clusterName}
        visible={visible}
        detail={currentRow}
        handleClose={handleClose}
      />
    </Root>
  );
}

export default connect((state) => ({
  nsOptions: state.util.nsOptions,
}))(Subscription);

const Root = styled.div`
  margin-top: 20px;
`;

const Icon = styled(SvgIcon)`
  cursor: pointer;
  color: #194dff;
`;
